/**
 * /plus (alias /pricing) — la page d'abonnement, côté parent.
 *
 * Structure reprise de memoneo (frontend/src/components/PricingContent.tsx :
 * promesse → cartes de formules → FAQ), ramenée à ce dont Chouette a besoin :
 * une seule formule payante, deux cartes, pas de comparatif en tableau, pas
 * de bascule mensuel/annuel. Sur un écran de 375 px les deux cartes
 * s'empilent ; elles passent côte à côte à partir de `md`.
 *
 * Décisions produit du 2026-09-21 tenues ici :
 *   — un seul abonnement, Chouette+ à 4,99 € TTC/mois ;
 *   — aucune mention de crédits face au parent, nulle part ;
 *   — pas de paiement en ligne pendant la bêta : le bouton affiche
 *     « Bientôt disponible », et « Chouette+ est actif » pour un abonné.
 *
 * La page est publique (hors `RequireAuth`) : `useAuth()` peut donc très bien
 * n'avoir aucun `me`, auquel cas on affiche simplement l'offre.
 */
import type { ReactNode } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, BookHeart, Lightbulb } from "lucide-react";
import { Container, PageLayout, Headline, Eyebrow, MutedText, Button, IconButton } from "../components/ui";
import { LegalFooter } from "../components/LegalFooter";
import { BrandMark } from "../components/Logo";
import { useAuth } from "../lib/auth";
import { isPlus } from "../lib/plan";
import { postEvent } from "../lib/dataSource";

interface FaqItem {
  q: string;
  a: string;
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {items.map((label, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm font-light leading-snug text-paper">
          <Check size={16} className="mt-0.5 shrink-0 text-mint" aria-hidden />
          <span>{label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Carte de formule. `featured` = la carte payante : bordure soleil et
 *  surimpression corail, la même hiérarchie que le bouton primaire. */
function PlanCard({
  name, price, priceNote, features, featured = false, badge, current = false, children,
}: {
  name: string;
  price: string;
  priceNote: string;
  features: string[];
  featured?: boolean;
  badge?: string;
  current?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`relative flex flex-col gap-4 rounded-card p-5 bg-surface overprint ${
        featured ? "border-2 border-sun ink-coral" : "border border-line ink-line"
      }`}
    >
      {badge && (
        <span className="seed-tag absolute -top-3 left-4">
          <span className="seed-tag__text">{badge}</span>
        </span>
      )}
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-heading font-bold text-lg text-paper">{name}</h3>
          {current && (
            <span className="seed-tag seed-tag--mint">
              <span className="seed-tag__text">{t("plus.currentPlan")}</span>
            </span>
          )}
        </div>
        <p className="mt-1 font-heading font-bold text-3xl text-sun">{price}</p>
        <MutedText className="mt-0.5">{priceNote}</MutedText>
      </div>
      <FeatureList items={features} />
      {children}
    </div>
  );
}

function Promise({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 text-sun mt-0.5" aria-hidden>{icon}</span>
      <div>
        <p className="font-heading font-semibold text-sm text-paper">{title}</p>
        <MutedText className="mt-0.5">{body}</MutedText>
      </div>
    </li>
  );
}

export default function ChouettePlus() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, isAuthenticated } = useAuth();
  const subscribed = isPlus(me);

  // Once per page view, however many times the disabled button's container
  // is clicked — plan Lot C.2: the only pre-Stripe signal of payment intent.
  const ctaSentRef = useRef(false);
  function handleCtaContainerClick(): void {
    if (ctaSentRef.current) return;
    ctaSentRef.current = true;
    void postEvent("plus_cta_clicked", { is_plus: subscribed });
  }

  // f2 ("4 histoires sur mesure offertes") a disparu avec D6 : plus rien
  // n'est offert à l'inscription, et le gratuit ne liste que 3 lignes.
  const freeFeatures = [1, 3, 4].map((n) => t(`plus.free.f${n}`));
  const plusFeatures = [1, 2, 3, 4].map((n) => t(`plus.paid.f${n}`));
  const faq = t("plus.faq", { returnObjects: true }) as unknown as FaqItem[];

  return (
    <PageLayout className="!pb-0">
      <Container className="pt-6 pb-12 flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <IconButton onClick={() => navigate(-1)} aria-label={t("common.back")}>
            <ArrowLeft size={18} />
          </IconButton>
          <BrandMark />
        </div>

        <header>
          <Eyebrow>{t("plus.eyebrow")}</Eyebrow>
          <Headline className="mt-1">{t("plus.title")}</Headline>
          <p className="mt-2 font-heading font-semibold text-base text-sun">{t("plus.tagline")}</p>
          <MutedText className="mt-2">{t("plus.intro")}</MutedText>
        </header>

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{t("plus.promiseTitle")}</h2>
          <ul className="flex flex-col gap-4">
            <Promise icon={<BookHeart size={20} />} title={t("plus.promise1Title")} body={t("plus.promise1Body")} />
            <Promise icon={<Sparkles size={20} />} title={t("plus.promise2Title")} body={t("plus.promise2Body")} />
            <Promise icon={<Lightbulb size={20} />} title={t("plus.promise3Title")} body={t("plus.promise3Body")} />
          </ul>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg mb-4">{t("plus.plansTitle")}</h2>
          <div className="flex flex-col gap-6 md:grid md:grid-cols-2 md:items-start">
            <PlanCard
              name={t("plus.free.name")}
              price={t("plus.free.price")}
              priceNote={t("plus.free.priceNote")}
              features={freeFeatures}
              current={isAuthenticated && !subscribed}
            />
            <PlanCard
              name={t("plus.paid.name")}
              price={t("plus.paid.price")}
              priceNote={t("plus.paid.priceNote")}
              features={plusFeatures}
              featured
              badge={subscribed ? undefined : t("plus.paid.badge")}
              current={subscribed}
            >
              {/* Emplacement du futur bouton Stripe : c'est ce <Button> qui
               *  deviendra « S'abonner » et appellera la création de session
               *  de paiement à l'ouverture publique. D'ici là il reste
               *  désactivé — aucun moyen de paiement n'est demandé pendant
               *  la bêta (décision du 2026-09-21). Le clic est capté sur ce
               *  conteneur, pas sur le <Button> : `disabled:pointer-events-
               *  none` (ui.tsx) laisse déjà passer le clic à travers le
               *  bouton désactivé jusqu'ici — c'est le seul signal
               *  d'intention de paiement avant Stripe (plan Lot C.2). */}
              <div className="flex flex-col gap-2" onClick={handleCtaContainerClick}>
                <Button className="w-full" disabled>
                  {subscribed ? t("plus.ctaActive") : t("plus.ctaSoon")}
                </Button>
                <MutedText className="text-center">
                  {subscribed ? t("plus.ctaActiveHint") : t("plus.ctaSoonHint")}
                </MutedText>
              </div>
            </PlanCard>
          </div>
        </section>

        <section>
          <h2 className="font-heading font-bold text-lg mb-3">{t("plus.faqTitle")}</h2>
          <div className="flex flex-col gap-2">
            {faq.map((item, i) => (
              <details
                key={i}
                className="group bg-surface border border-line rounded-card overprint ink-line px-4 py-3"
              >
                <summary className="tap-target cursor-pointer list-none font-heading font-semibold text-sm text-paper flex items-center justify-between gap-3">
                  {item.q}
                  <span className="text-sun text-lg leading-none group-open:hidden" aria-hidden>+</span>
                  <span className="text-sun text-lg leading-none hidden group-open:inline" aria-hidden>−</span>
                </summary>
                <p className="mt-2 text-sm text-violet-soft font-light leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
          <MutedText className="mt-3">
            {t("plus.dataNote")}{" "}
            <Link to="/privacy" className="text-sun underline underline-offset-2">
              {t("footer.privacy")}
            </Link>
            .
          </MutedText>
        </section>

        <LegalFooter />
      </Container>
    </PageLayout>
  );
}
